<template>
    <div class="knowledge-page">
        <!-- 顶部：全局清洗进度大卡片（与选中单元无关） -->
        <div class="global-stats-card" v-loading="globalStatsLoading">
            <div class="gsc-title">清洗进度</div>
            <div class="gsc-body">
                <!-- 去重进度（T1） -->
                <div class="gsc-item gsc-dedup">
                    <span class="gsc-num">{{ globalStats.dedupCompleted }}<b>/{{ globalStats.dedupTotal }}</b></span>
                    <span class="gsc-label">去重（T1）</span>
                    <span class="gsc-meta">未完成 {{ globalStats.pendingDedup }}</span>
                    <span class="gsc-rate">完成率 {{ globalStats.dedupRate }}%</span>
                </div>
                <!-- 诊断进度（T2） -->
                <div class="gsc-item gsc-diagnose">
                    <span class="gsc-num">{{ globalStats.diagCompleted }}<b>/{{ globalStats.diagTotal }}</b></span>
                    <span class="gsc-label">诊断（T2）</span>
                    <span class="gsc-meta">未完成 {{ globalStats.pendingDiagnose }}</span>
                    <span class="gsc-rate">完成率 {{ globalStats.diagRate }}%</span>
                </div>
                <!-- 单单词题型进度 -->
                <div class="gsc-item gsc-perword">
                    <span class="gsc-num">{{ globalStats.perWordCompleted }}<b>/{{ globalStats.perWordTotal }}</b></span>
                    <span class="gsc-label">单单词题型</span>
                    <span class="gsc-meta">未完成 {{ globalStats.perWordTotal - globalStats.perWordCompleted }}</span>
                    <span class="gsc-rate">完成率 {{ globalStats.perWordCompletionRate }}%</span>
                </div>
            </div>
        </div>

        <!-- 第二块：单单词题型补齐任务量（每种题型一个卡片） -->
        <div class="gen-task-card" v-loading="globalStatsLoading">
            <div class="gtc-title gtc-title-with-sub">
                <div class="gtc-title-main">
                    <span>单单词题型补齐任务</span>
                    <el-button
                        type="warning"
                        size="mini"
                        icon="el-icon-s-tools"
                        class="skip-manage-btn"
                        @click="openFailManage">
                        跳过管理
                    </el-button>
                </div>
                <span class="gtc-sub" v-if="globalStats.perWordTaskTarget">目标任务 {{ globalStats.perWordTaskCurrent }}/{{ globalStats.perWordTaskTarget }}（完成率 {{ globalStats.perWordTaskRate }}%）</span>
            </div>
            <div class="gtc-body">
                <div class="gtc-item" v-for="item in perWordTypeCards" :key="'pw-'+item.type">
                    <div class="gtc-item-head">
                        <span class="gtc-num">{{ item.current }}<b>/{{ item.need }}</b></span>
                        <span class="gtc-label">{{ questionTypeLabel(item.type) }}</span>
                    </div>
                    <div class="gtc-row">配置数量：{{ item.targetStock }}</div>
                    <div class="gtc-row">总需要：{{ item.need }}</div>
                    <div class="gtc-row gtc-pending">待补充：{{ item.pending }}</div>
                    <div class="gtc-row gtc-rate">完成率：{{ item.rate }}%</div>
                </div>
            </div>
        </div>

        <!-- 单单词补齐失败跳过管理弹窗 -->
        <el-dialog
            title="单单词补齐 · 跳过管理"
            :visible.sync="failManageVisible"
            width="760px"
            @open="loadFailList">
            <el-alert
                type="warning"
                show-icon
                :closable="false"
                class="fail-tip"
                title="以下(单词,题型)因 AI 内容生成失败连续达到阈值被自动跳过，不再占用补齐任务。确认已可正常生成后，可手动解除恢复参与补齐。" />
            <el-table
                :data="failList"
                border
                stripe
                size="medium"
                v-loading="failLoading"
                style="width:100%">
                <el-table-column label="序号" type="index" width="60" align="center" />
                <el-table-column prop="word" label="单词" min-width="110">
                    <template slot-scope="scope">
                        <span class="word-text">{{ scope.row.word || ('#' + scope.row.wordId) }}</span>
                    </template>
                </el-table-column>
                <el-table-column prop="chineseMeaning" label="中文释义" min-width="130" show-overflow-tooltip />
                <el-table-column label="题型" width="110" align="center">
                    <template slot-scope="scope">
                        {{ questionTypeLabel(scope.row.questionType) }}
                    </template>
                </el-table-column>
                <el-table-column prop="failCount" label="失败次数" width="90" align="center" />
                <el-table-column label="最近失败时间" width="170" align="center">
                    <template slot-scope="scope">
                        {{ scope.row.lastFailTime || '-' }}
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="90" align="center">
                    <template slot-scope="scope">
                        <el-button
                            type="text"
                            size="small"
                            class="act-reset"
                            :loading="failResettingId === (scope.row.wordId + '|' + scope.row.questionType)"
                            @click="onResetFail(scope.row)">
                            解除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>
            <div class="pagination-wrap">
                <el-pagination
                    background
                    :current-page="failPage"
                    :page-size="failPageSize"
                    :total="failTotal"
                    layout="total, prev, pager, next"
                    @current-change="onFailPageChange" />
            </div>
        </el-dialog>

        <!-- 第三块：短文填空补齐任务（T5）：4词/组，独立统计展示 -->
        <div class="gen-task-card gtc-passagefill-card" v-loading="globalStatsLoading">
            <div class="gtc-title gtc-title-with-sub">
                <div class="gtc-title-main">
                    <span>短文填空补齐（T5）</span>
                </div>
                <span class="gtc-sub" v-if="globalStats.passageFillTotal">总组合数 {{ globalStats.passageFillTotal }} · 目标任务 {{ globalStats.passageFillTaskCurrent }}/{{ globalStats.passageFillTaskTarget }}（完成率 {{ globalStats.passageFillTaskRate }}%）</span>
            </div>
            <div class="gtc-body">
                <div class="gtc-item gtc-passagefill gtc-clickable" v-for="item in passageFillTypeCards" :key="'pf-'+item.type" @click="openComboDrill(item.type)">
                    <div class="gtc-item-head">
                        <span class="gtc-num">{{ item.current }}<b>/{{ item.need }}</b></span>
                        <span class="gtc-label">{{ questionTypeLabel(item.type) }}</span>
                    </div>
                    <div class="gtc-row">配置数量：{{ item.targetStock }}/组</div>
                    <div class="gtc-row">总需要：{{ item.need }}</div>
                    <div class="gtc-row gtc-pending">待补充：{{ item.pending }}</div>
                    <div class="gtc-row gtc-rate">完成率：{{ item.rate }}%</div>
                    <div class="gtc-row gtc-drill-tip">点击查看单词组 ▸</div>
                </div>
                <div class="gtc-item gtc-empty" v-if="!passageFillTypeCards.length">
                    <div class="gtc-row gtc-todo">未配置短文填空题型或单词不足一组</div>
                </div>
            </div>
        </div>

        <!-- 第四块：组合题型补齐任务量（每个组合题型一个卡片，来自后端 comboTypeDetail 真实统计） -->
        <div class="gen-task-card" v-loading="globalStatsLoading">
            <div class="gtc-title gtc-title-with-sub">
                <div class="gtc-title-main">
                    <span>组合题型补齐任务</span>
                </div>
                <span class="gtc-sub" v-if="globalStats.comboTotal">总组合数 {{ globalStats.comboTotal }} · 目标 {{ globalStats.comboTaskCurrent }}/{{ globalStats.comboTaskTarget }}（完成率 {{ globalStats.comboTaskRate }}%）</span>
            </div>
            <div class="gtc-body">
                <div class="gtc-item gtc-clickable" v-for="ct in comboTypeCards" :key="'cb-'+ct.type" @click="openComboDrill(ct.type)">
                    <div class="gtc-item-head">
                        <span class="gtc-num">{{ ct.current }}<b>/{{ ct.need }}</b></span>
                        <span class="gtc-label">{{ questionTypeLabel(ct.type) }}</span>
                    </div>
                    <div class="gtc-row">配置数量：{{ ct.targetStock }}/组合</div>
                    <div class="gtc-row">总需要：{{ ct.need }}</div>
                    <div class="gtc-row gtc-pending">待补充：{{ ct.pending }}</div>
                    <div class="gtc-row gtc-rate">完成率：{{ ct.rate }}%</div>
                    <div class="gtc-row gtc-drill-tip">点击查看单词组 ▸</div>
                </div>
                <div class="gtc-item gtc-empty" v-if="!comboTypeCards.length">
                    <div class="gtc-row gtc-todo">未配置组合题型或单词不足一个组合</div>
                </div>
            </div>
        </div>

        <!-- 第四块下钻：组合题型 → 年级 → 分册 → 单词组 → 试题（单弹窗三段布局） -->
        <el-dialog
            :title="comboDrillTitle"
            :visible.sync="comboDrillVisible"
            width="1180px"
            top="5vh"
            @close="closeComboDrill">
            <div class="combo-drill">
                <!-- 最左：年级列表 -->
                <div class="cd-grade" v-loading="comboTreeLoading">
                    <div class="cd-col-title">年级</div>
                    <div class="cd-grade-list">
                        <div
                            v-for="item in gradeGroups"
                            :key="item.gradeId"
                            class="cd-grade-item"
                            :class="{ active: comboDrillGradeId === item.gradeId }"
                            @click="onPickGrade(item)">
                            <span class="cd-grade-name">{{ item.gradeName || ('年级'+item.gradeId) }}</span>
                            <span class="cd-grade-count">{{ item.comboCount }} 组</span>
                        </div>
                        <div v-if="!comboTreeList.length && !comboTreeLoading" class="cd-key-empty">该题型暂无组合题型试题</div>
                    </div>
                </div>
                <!-- 中：分册 + 单词组列表 -->
                <div class="cd-left" v-loading="comboKeyLoading">
                    <div class="cd-col-title">分册 · 单词组（共 {{ comboKeyTotal }} 组）</div>
                    <div class="cd-volume-list">
                        <div
                            v-for="v in volumeGroups"
                            :key="v.volume"
                            class="cd-volume-item"
                            :class="{ active: comboDrillVolume === v.volume }"
                            @click="onPickGradeVolume(v)">
                            {{ v.volume || '未分册' }}（{{ v.comboCount }} 组）
                        </div>
                    </div>
                    <div class="cd-key-list">
                        <div
                            v-for="item in comboKeyList"
                            :key="item.comboKey"
                            class="cd-key-item"
                            :class="{ active: comboDrillKey === item.comboKey }"
                            @click="onPickComboKey(item)">
                            <div class="cd-key-main">
                                <span class="cd-key-words">{{ formatComboWords(item.wordDetails) }}</span>
                                <span class="cd-key-count">{{ item.questionCount }} 题</span>
                            </div>
                        </div>
                        <div v-if="!comboKeyList.length && !comboKeyLoading" class="cd-key-empty">该分册暂无组合题型试题</div>
                    </div>
                    <div class="pagination-wrap">
                        <el-pagination
                            background
                            small
                            :current-page="comboKeyPage"
                            :page-size="comboKeyPageSize"
                            :total="comboKeyTotal"
                            layout="prev, pager, next"
                            @current-change="onComboKeyPageChange" />
                    </div>
                </div>
                <!-- 右：试题列表 -->
                <div class="cd-right" v-loading="comboQuestionLoading">
                    <div class="cd-right-title" v-if="comboDrillKey">
                        试题（单词组：{{ formatComboWords(currentComboWords) }}）
                    </div>
                    <div class="cd-right-title cd-right-empty" v-else>请选择左侧单词组查看试题</div>
                    <el-table
                        v-if="comboDrillKey"
                        :data="comboQuestionList"
                        border
                        stripe
                        size="small"
                        class="panel-table"
                        style="width:100%">
                        <el-table-column label="序号" type="index" width="50" align="center" />
                        <el-table-column prop="content" label="内容" min-width="240" show-overflow-tooltip />
                        <el-table-column prop="translation" label="翻译" min-width="120" show-overflow-tooltip />
                        <el-table-column label="状态" width="100" align="center">
                            <template slot-scope="scope">
                                <el-tag
                                    size="mini"
                                    :type="scope.row.status === 2 ? 'danger' : (scope.row.reviewStatus === 2 ? 'success' : (scope.row.reviewStatus === 1 ? 'info' : 'warning'))"
                                    effect="plain">
                                    {{ scope.row.status === 2 ? '已废弃' : (scope.row.reviewStatus === 2 ? '二次诊断' : (scope.row.reviewStatus === 1 ? 'AI已诊断' : '未诊断')) }}
                                </el-tag>
                            </template>
                        </el-table-column>
                    </el-table>
                    <div class="pagination-wrap" v-if="comboDrillKey">
                        <el-pagination
                            background
                            small
                            :current-page="comboQuestionPage"
                            :page-size="comboQuestionPageSize"
                            :total="comboQuestionTotal"
                            layout="total, prev, pager, next"
                            @current-change="onComboQuestionPageChange" />
                    </div>
                </div>
            </div>
        </el-dialog>


        <div class="kb-main">
            <!-- 左侧：资源树 -->
            <div class="tree-panel">
            <div class="tree-head">
                <span class="tree-title">知识库</span>
                <el-tooltip content="清除缓存并重新从数据库加载结构" placement="top">
                    <el-button type="text" icon="el-icon-refresh" size="mini" @click="onClearCache">清缓存</el-button>
                </el-tooltip>
            </div>
            <div class="tree-search">
                <el-input
                    v-model="treeKeyword"
                    placeholder="搜索单词名（全局）"
                    clearable
                    prefix-icon="el-icon-search"
                    size="small"
                    @input="onTreeSearch"
                    @clear="onTreeSearch" />
            </div>
            <!-- 搜索结果列表 -->
            <div v-if="searchActive" class="search-result" v-loading="searchLoading">
                <div v-if="searchList.length === 0 && !searchLoading" class="search-empty">未找到匹配单词</div>
                <div
                    v-for="item in searchList"
                    :key="item.id"
                    class="search-item"
                    @click="onPickSearchWord(item)">
                    <div class="si-word">{{ item.word }}</div>
                    <div class="si-meta">{{ item.gradeName }} / {{ item.volume }} / {{ item.unitName }}</div>
                    <div class="si-meaning">{{ item.chineseMeaning }}</div>
                </div>
            </div>
            <!-- 树 -->
            <div v-else class="tree-body" v-loading="treeLoading">
                <el-tree
                    ref="treeRef"
                    :data="treeData"
                    :props="treeProps"
                    node-key="nodeId"
                    :expand-on-click-node="true"
                    :default-expanded-keys="defaultExpandedKeys"
                    :default-expand-all="false"
                    highlight-current
                    class="kb-tree"
                    @node-click="onNodeClick">
                    <span class="tree-node" slot-scope="{ node, data }">
                        <i :class="nodeIcon(data)"></i>
                        <span class="tree-label">{{ data.label }}</span>
                    </span>
                </el-tree>
            </div>
        </div>

        <!-- 右侧：内容面板 -->
        <div class="content-panel">
            <!-- 初始空状态 -->
            <el-empty
                v-if="!activeUnitId && !currentWord"
                description="请选择左侧年级、分册下的单元，查看该单元单词的配套题目"
                class="content-empty" />

            <!-- 单元单词列表 -->
            <template v-if="activeUnitId && !currentWord">
                <div class="panel-header">
                    <span class="panel-title">单词列表</span>
                    <span class="total-badge">共 <b>{{ wordTotal }}</b> 个单词</span>
                    <div class="header-right">
                            <el-input
                                v-model="wordKeyword"
                                placeholder="筛选当前单元单词"
                                clearable
                                prefix-icon="el-icon-search"
                                size="small"
                                style="width:200px"
                                @input="onWordSearch"
                                @clear="onWordSearch" />
                    </div>
                </div>

                <!-- 单元级清洗状态统计卡片 -->
                <div class="unit-stats-bar" v-loading="unitStatsLoading">
                    <div class="us-item us-total" :class="{ on: unitStats.total }">
                        <span class="us-num">{{ unitStats.total }}</span>
                        <span class="us-label">总题目</span>
                    </div>
                    <div class="us-item us-un" :class="{ on: unitStats.unReviewed }" @click="filterByStatus('unReviewed')">
                        <span class="us-num">{{ unitStats.unReviewed }}</span>
                        <span class="us-label">未诊断</span>
                    </div>
                    <div class="us-item us-ai" :class="{ on: unitStats.aiReviewed }" @click="filterByStatus('aiReviewed')">
                        <span class="us-num">{{ unitStats.aiReviewed }}</span>
                        <span class="us-label">AI已诊断</span>
                    </div>
                    <div class="us-item us-te" :class="{ on: unitStats.teacherReviewed }" @click="filterByStatus('teacherReviewed')">
                        <span class="us-num">{{ unitStats.teacherReviewed }}</span>
                        <span class="us-label">二次确认</span>
                    </div>
                    <div class="us-item us-pending" :class="{ on: unitStats.pendingManualConfirm }" @click="filterByStatus('pendingManualConfirm')">
                        <span class="us-num">{{ unitStats.pendingManualConfirm }}</span>
                        <span class="us-label">待人工确认</span>
                    </div>
                    <div class="us-item us-dup" :class="{ on: unitStats.duplicateDiscarded }" @click="filterByStatus('duplicateDiscarded')">
                        <span class="us-num">{{ unitStats.duplicateDiscarded }}</span>
                        <span class="us-label">已废弃重复</span>
                    </div>
                </div>
                <el-table
                    :data="wordList"
                    border
                    stripe
                    size="medium"
                    class="panel-table"
                    v-loading="wordLoading"
                    @row-click="onWordRowClick"
                    style="width:100%">
                    <el-table-column label="序号" type="index" min-width="10%" align="center" />
                    <el-table-column prop="word" label="单词" min-width="10%">
                        <template slot-scope="scope">
                            <span class="word-text">{{ scope.row.word }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="chineseMeaning" label="中文释义" min-width="10%" show-overflow-tooltip />
                    <el-table-column prop="partOfSpeech" label="词性" min-width="10%" align="center" />
                    <el-table-column label="清洗状态" min-width="22%" align="center">
                        <template slot-scope="scope">
                            <div class="word-review-stats" v-if="reviewStatsMap[scope.row.id]">
                                <span class="wrs-tag wrs-un" :class="{ zero: !reviewStatsMap[scope.row.id].unReviewed }">未{{ reviewStatsMap[scope.row.id].unReviewed }}</span>
                                <span class="wrs-tag wrs-ai" :class="{ zero: !reviewStatsMap[scope.row.id].aiReviewed }">AI{{ reviewStatsMap[scope.row.id].aiReviewed }}</span>
                                <span class="wrs-tag wrs-te" :class="{ zero: !reviewStatsMap[scope.row.id].teacherReviewed }">确{{ reviewStatsMap[scope.row.id].teacherReviewed }}</span>
                                <span class="wrs-tag wrs-pen" :class="{ zero: !reviewStatsMap[scope.row.id].pendingManualConfirm }">待{{ reviewStatsMap[scope.row.id].pendingManualConfirm }}</span>
                                <span class="wrs-tag wrs-dup" :class="{ zero: !reviewStatsMap[scope.row.id].duplicateDiscarded }">废{{ reviewStatsMap[scope.row.id].duplicateDiscarded }}</span>
                            </div>
                            <span v-else class="kb-empty">加载中…</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="音标" min-width="10%">
                        <template slot-scope="scope">
                            <span class="phonetic">{{ scope.row.phoneticUk || '-' }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="知识库" min-width="40%" align="center">
                        <template slot-scope="scope">
                            <div class="kb-stats-mini">
                                <span
                                    v-for="(cnt, type) in wordStatsMap[scope.row.id] || {}"
                                    :key="type"
                                    class="kb-type-tag"
                                    :class="'type-' + type"
                                    :title="questionTypeLabel(type) + ': ' + cnt + '条'">
                                    {{ questionTypeShort(type) }}<b>{{ cnt }}</b>
                                </span>
                                <span v-if="!wordStatsMap[scope.row.id] || Object.keys(wordStatsMap[scope.row.id] || {}).length === 0" class="kb-empty">暂无</span>
                            </div>
                        </template>
                    </el-table-column>
<!--                    <el-table-column label="操作" min-width="10%" align="center" fixed="right">-->
<!--                        <template slot-scope="scope">-->
<!--                            <el-button-->
<!--                                type="primary"-->
<!--                                size="small"-->
<!--                                icon="el-icon-view"-->
<!--                                @click="openDetail(scope.row)">-->
<!--                                查看题目-->
<!--                            </el-button>-->
<!--                        </template>-->
<!--                    </el-table-column>-->
                </el-table>
                <div class="pagination-wrap">
                    <el-pagination
                        background
                        :current-page="wordPage"
                        :page-size="wordPageSize"
                        :total="wordTotal"
                        layout="total, prev, pager, next"
                        @current-change="onWordPageChange" />
                </div>
            </template>

            <!-- 单词知识库详情（右侧抽屉） -->
            <el-drawer
                :visible.sync="detailDrawerVisible"
                direction="rtl"
                size="50%"
                :title="currentWord ? (currentWord.word + ' · 配套题目') : '配套题目'"
                @close="backToList"
                class="kb-drawer">
                <div class="drawer-body" v-if="currentWord">
                    <div class="word-info-bar">
                        <span class="wi-word">{{ currentWord.word }}</span>
                        <el-divider direction="vertical" />
                        <span class="wi-chinese">{{ currentWord.chineseMeaning }}</span>
                        <el-divider direction="vertical" />
                        <span class="wi-pos">{{ currentWord.partOfSpeech }}</span>
                        <el-divider direction="vertical" />
                        <span class="wi-phonetic">英 {{ currentWord.phoneticUk || '-' }} 美 {{ currentWord.phoneticUs || '-' }}</span>
                    </div>

                    <div class="review-summary">
                        <span class="rs-item">共 <b>{{ detailTotal }}</b> 题</span>
                        <span class="rs-item rs-un" :class="{ on: reviewSummary.unReviewed }">未诊断 <b>{{ reviewSummary.unReviewed }}</b></span>
                        <span class="rs-item rs-ai" :class="{ on: reviewSummary.aiReviewed }">AI已诊断 <b>{{ reviewSummary.aiReviewed }}</b></span>
                        <span class="rs-item rs-te" :class="{ on: reviewSummary.teacherReviewed }">二次诊断 <b>{{ reviewSummary.teacherReviewed }}</b></span>
                        <span class="rs-item rs-pending" :class="{ on: reviewSummary.pendingManualConfirm }">待人工确认 <b>{{ reviewSummary.pendingManualConfirm }}</b></span>
                        <span class="rs-item rs-dup" :class="{ on: reviewSummary.duplicate }">重复题 <b>{{ reviewSummary.duplicate }}</b></span>
                        <el-button
                            type="success"
                            size="small"
                            icon="el-icon-plus"
                            class="gen-btn"
                            @click="openGenerateDialog">
                            手动补齐生成
                        </el-button>
                        <span class="include-discarded">
                            <el-switch
                                v-model="includeDiscarded"
                                size="small"
                                @change="onToggleIncludeDiscarded" />
                            <span class="id-label">包含已废弃</span>
                        </span>
                    </div>

                    <el-alert
                        type="info"
                        :closable="false"
                        show-icon
                        class="action-hint">
                        <div slot="title">
                            <b>操作说明</b>：
                            <b>看修订</b> 查看 AI 对题目内容/翻译/解析的修订前后对比；
                            <b>确认诊断</b> 将 AI 已诊断题标记为老师二次确认（已诊断），已确认后可撤销；
                            <b>重新诊断</b> 调用 AI 对该题重新进行质量诊断与修订。
                        </div>
                    </el-alert>

                    <div class="type-tabs">
                        <el-tag
                            v-for="t in detailTypeTabs"
                            :key="t.type"
                            :type="detailActiveType === t.type ? '' : 'info'"
                            :effect="detailActiveType === t.type ? 'dark' : 'plain'"
                            :class="['type-tab', 'type-tab-' + t.type]"
                            @click="switchDetailType(t.type)">
                            {{ t.label }}
                            <span class="tab-count">{{ t.count }}条</span>
                        </el-tag>
                    </div>

                    <!-- 手动补齐生成弹窗 -->
                    <el-dialog
                        title="手动补齐生成"
                        :visible.sync="genDialogVisible"
                        append-to-body
                        width="420px">
                        <div class="gen-dialog">
                            <el-alert
                                type="info"
                                :closable="false"
                                show-icon
                                style="margin-bottom:14px">
                                <div slot="title">
                                    将为单词「<b>{{ currentWord ? currentWord.word : '' }}</b>」生成单词语种配套题目并入库，
                                    生成后进入诊断/去重流水线。段落/多词组合类题型由定时任务自动补齐。
                                </div>
                            </el-alert>
                            <el-form label-width="80px" size="small">
                                <el-form-item label="题型">
                                    <el-select v-model="genForm.questionType" placeholder="请选择题型" style="width:100%">
                                        <el-option
                                            v-for="t in genTypeOptions"
                                            :key="t.type"
                                            :label="t.label"
                                            :value="t.type" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="生成数量">
                                    <el-input-number
                                        v-model="genForm.count"
                                        :min="1"
                                        :max="20"
                                        :step="1"
                                        style="width:140px" />
                                    <span class="gen-tip">（范围 1~20，默认 10）</span>
                                </el-form-item>
                            </el-form>
                        </div>
                        <span slot="footer" class="dialog-footer">
                            <el-button size="small" @click="genDialogVisible = false">取消</el-button>
                            <el-button size="small" type="primary" :loading="genSubmitting" @click="submitGenerate">
                                确认生成
                            </el-button>
                        </span>
                    </el-dialog>

                    <el-table
                        :data="detailList"
                        border
                        stripe
                        size="small"
                        v-loading="detailLoading"
                        class="panel-table"
                        style="width:100%">
                        <el-table-column label="序号" type="index" width="50" align="center" />
                        <el-table-column prop="content" label="内容" min-width="220" show-overflow-tooltip />
                        <el-table-column prop="translation" label="翻译" min-width="120" show-overflow-tooltip />
                        <el-table-column label="状态" width="110" align="center" fixed="right">
                            <template slot-scope="scope">
                                <el-tag
                                    size="mini"
                                    :type="scope.row.status === 2 ? 'danger' : (scope.row.isPendingManual ? 'warning' : (scope.row.reviewStatus === 2 ? 'success' : (scope.row.reviewStatus === 1 ? 'info' : 'warning')))"
                                    effect="plain">
                                    {{ scope.row.status === 2 ? '已废弃' : (scope.row.isPendingManual ? '待人工确认' : (scope.row.reviewStatus === 2 ? '二次诊断' : (scope.row.reviewStatus === 1 ? 'AI已诊断' : '未诊断'))) }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column label="操作" width="240" align="center" fixed="right">
                            <template slot-scope="scope">
                                <div class="row-actions">
                                    <el-button type="text" size="small" @click="openAiCompare(scope.row)">看修订</el-button>
                                    <el-button
                                        type="text"
                                        size="small"
                                        class="act-confirm"
                                        :class="{ 'is-revoke': scope.row.reviewStatus === 2 }"
                                        @click="onTeacherConfirm(scope.row)"
                                        :disabled="scope.row.reviewStatus === 0">
                                        {{ scope.row.reviewStatus === 2 ? '撤销诊断' : '确认诊断' }}
                                    </el-button>
                                    <el-button type="text" size="small" @click="onReDiagnose(scope.row)">重新诊断</el-button>
                                    <el-button type="text" size="small" v-if="scope.row.duplicateOf" @click="onResolveDuplicate(scope.row)">重复处理</el-button>
                                    <el-button
                                        type="text"
                                        size="small"
                                        class="act-pending"
                                        v-if="scope.row.isPendingManual"
                                        @click="onConfirmPending(scope.row)">
                                        确认废弃
                                    </el-button>
                                    <el-popconfirm
                                        title="确定删除该知识库记录吗？"
                                        confirm-button-text="删除"
                                        cancel-button-text="取消"
                                        @confirm="handleDeleteItem(scope.row)">
                                        <el-button slot="reference" type="text" size="small" class="act-delete">
                                            删除
                                        </el-button>
                                    </el-popconfirm>
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>

                    <div class="pagination-wrap" style="margin-top:15px">
                        <el-pagination
                            background
                            small
                            :current-page="detailPage"
                            :page-size="detailPageSize"
                            :total="detailTotal"
                            layout="total, prev, pager, next"
                            @current-change="onDetailPageChange" />
                    </div>
                </div>

                <!-- AI 修订前后对比 -->
                <el-dialog
                    title="AI 修订对比"
                    :visible.sync="aiCompareVisible"
                    append-to-body
                    width="760px">
                    <div v-if="aiCompareRow" class="ai-compare">
                        <div class="ac-row">
                            <div class="ac-label">题目内容</div>
                            <div class="ac-cols">
                                <div class="ac-col">
                                    <div class="ac-h">修订前</div>
                                    <div class="ac-text" v-html="renderDiff(aiCompareRow.content, aiCompareRow.aiRevisedContent, 'old')" />
                                </div>
                                <div class="ac-col ac-new">
                                    <div class="ac-h">AI 修订后</div>
                                    <div class="ac-text" v-html="renderDiff(aiCompareRow.content, aiCompareRow.aiRevisedContent, 'new')" />
                                </div>
                            </div>
                        </div>
                        <div class="ac-row">
                            <div class="ac-label">中文翻译</div>
                            <div class="ac-cols">
                                <div class="ac-col">
                                    <div class="ac-h">修订前</div>
                                    <div class="ac-text" v-html="renderDiff(aiCompareRow.translation, aiCompareRow.aiRevisedTranslation, 'old')" />
                                </div>
                                <div class="ac-col ac-new">
                                    <div class="ac-h">AI 修订后</div>
                                    <div class="ac-text" v-html="renderDiff(aiCompareRow.translation, aiCompareRow.aiRevisedTranslation, 'new')" />
                                </div>
                            </div>
                        </div>
                        <div class="ac-row" v-if="aiCompareRow.answerAnalysis || aiCompareRow.aiRevisedAnalysis">
                            <div class="ac-label">答案解析</div>
                            <div class="ac-cols">
                                <div class="ac-col">
                                    <div class="ac-h">修订前</div>
                                    <div class="ac-text" v-html="renderDiff(aiCompareRow.answerAnalysis, aiCompareRow.aiRevisedAnalysis, 'old')" />
                                </div>
                                <div class="ac-col ac-new">
                                    <div class="ac-h">AI 修订后</div>
                                    <div class="ac-text" v-html="renderDiff(aiCompareRow.answerAnalysis, aiCompareRow.aiRevisedAnalysis, 'new')" />
                                </div>
                            </div>
                        </div>
                        <div class="ac-meta">
                            <span>AI 诊断评分：{{ aiCompareRow.aiScore != null ? aiCompareRow.aiScore : '-' }}</span>
                            <span v-if="aiCompareRow.aiReviewTime">诊断时间：{{ aiCompareRow.aiReviewTime }}</span>
                        </div>
                    </div>
                </el-dialog>
            </el-drawer>
        </div>
        </div><!-- /kb-main -->
    </div>
</template>

<script>
import { reqResourceTree, reqClearTreeCache, reqSearchWordByName } from '@/api/education/word'
import { reqWordListByUnit } from '@/api/education/word'
import { reqPageByWord, reqStatsByWord, reqDeleteQuestion, reqQuestionDetail, reqConfirmTeacherReview, reqRevokeTeacherReview, reqResolveDuplicate, reqReDiagnose, reqStatsReviewByUnit, reqStatsReviewByWords, reqGenerateForCombo, reqStatsByWordIdsBatch, reqStatsGlobal, reqGenerateFailPage, reqGenerateFailReset, reqComboKeys, reqPageByCombo, reqComboTree } from '@/api/education/questionLibrary'

// 题型映射（与后端 QuestionType 枚举保持一致：1-高频生词表,2-SuperMemo速记,3-例句练习表,4-好词好句,5-语境语感训练,6-选词填空,7-短文填空,8-任务型阅读,9-完形填空,10-补全对话）
const QUESTION_TYPE_MAP = {
    1: { label: '高频生词表', short: '生词' },
    2: { label: 'SuperMemo速记', short: '速记' },
    3: { label: '例句练习表', short: '例句表' },
    4: { label: '好词好句', short: '好句' },
    5: { label: '语境语感训练', short: '语感' },
    6: { label: '选词填空', short: '选词' },
    7: { label: '短文填空', short: '短文' },
    8: { label: '任务型阅读', short: '阅读' },
    9: { label: '完形填空', short: '完形' },
    10: { label: '补全对话', short: '对话' }
}

export default {
    name: 'KnowledgeLibrary',
    data() {
        return {
            // 树
            treeData: [],
            treeLoading: false,
            defaultExpandedKeys: [],
            treeProps: { children: 'children', label: 'label' },
            treeKeyword: '',
            searchActive: false,
            searchList: [],
            searchLoading: false,

            // 右侧状态
            activeGradeId: null,
            activeVolume: null,
            activeUnitId: null,

            // 单词表格
            wordKeyword: '',
            wordList: [],
            wordTotal: 0,
            wordPage: 1,
            wordPageSize: 10,
            wordLoading: false,
            wordStatsMap: {},
            reviewStatsMap: {},
            // 单元级统计
            unitStats: { total: 0, unReviewed: 0, aiReviewed: 0, teacherReviewed: 0, pendingManualConfirm: 0, duplicateDiscarded: 0 },
            unitStatsLoading: false,
            // 单元筛选状态：点击统计卡片时设置，null=不过滤
            unitFilterStatus: null,

            // 全局清洗进度（顶部大卡片，与选中单元无关）
            globalStats: {
                dedupTotal: 0, dedupCompleted: 0, pendingDedup: 0, dedupRate: 0,
                diagTotal: 0, diagCompleted: 0, pendingDiagnose: 0, diagRate: 0,
                perWordTotal: 0, perWordCompleted: 0, perWordCompletionRate: 0,
                // T3 单单词补齐任务量
                enabledWordCount: 0, perWordTypeCount: 0, perWordTargetStock: 0,
                perWordTaskTarget: 0, perWordTaskCurrent: 0, perWordTaskRate: 0,
                perWordTypeDetail: [],
                // T3 组合题型补齐任务量
                comboTotal: 0, comboTargetStock: 0, comboTypeCount: 0,
                comboTaskTarget: 0, comboTaskCurrent: 0, comboTaskRate: 0,
                comboTypeDetail: [],
                // T5 短文填空补齐统计（独立统计，附加在单单词题型补齐任务区块内展示）
                passageFillTotal: 0, passageFillTargetStock: 0, passageFillTypeCount: 0,
                passageFillTaskTarget: 0, passageFillTaskCurrent: 0, passageFillTaskRate: 0,
                passageFillTypeDetail: []
            },
            globalStatsLoading: false,

            // 详情
            currentWord: null,
            detailDrawerVisible: false,
            detailActiveType: null,
            detailTypeTabs: [],
            reviewSummary: { unReviewed: 0, aiReviewed: 0, teacherReviewed: 0, duplicate: 0, pendingManualConfirm: 0 },
            detailList: [],
            detailTotal: 0,
            detailPage: 1,
            detailPageSize: 10,
            detailLoading: false,
            // 单元筛选状态：从统计卡片跳转时设置
            detailFilterStatus: null,
            // 抽屉默认仅显示启用题；true 时包含已废弃重复题(status=2)，供老师查看/恢复
            includeDiscarded: false,

            // AI 对比
            aiCompareVisible: false,
            aiCompareRow: null,

            // 手动补齐生成
            genDialogVisible: false,
            genSubmitting: false,
            genForm: { questionType: 4, count: 10 },
            // 手动补齐仅处理单个单词的 AI 题型（2/3/4/5/6）：段落/多词组合类(7/8/9/10)由定时任务 T3 负责
            // 注：1-高频生词表、2-SuperMemo速记为内置非AI题型，不在此 AI 补齐入口暴露
            genTypeOptions: [
                { type: 3, label: '例句练习表' },
                { type: 4, label: '好词好句' },
                { type: 5, label: '语境语感训练' },
                { type: 6, label: '选词填空' }
            ],

            // 单单词补齐失败跳过管理
            failManageVisible: false,
            failList: [],
            failTotal: 0,
            failPage: 1,
            failPageSize: 10,
            failLoading: false,
            failResettingId: null,

            // 第四块组合题型下钻：题型 → 年级 → 分册 → 单词组 → 试题（单弹窗左右布局）
            comboDrillVisible: false,
            comboDrillType: null,
            comboDrillTitle: '',
            comboTreeList: [],   // 三级层级：[{gradeId, gradeName, volume, comboCount}]
            comboTreeLoading: false,
            comboDrillGradeId: null,
            comboDrillVolume: null,
            comboKeyList: [],
            comboKeyTotal: 0,
            comboKeyPage: 1,
            comboKeyPageSize: 20,
            comboKeyLoading: false,
            comboDrillKey: null,
            currentComboWords: '',
            comboQuestionList: [],
            comboQuestionTotal: 0,
            comboQuestionPage: 1,
            comboQuestionPageSize: 10,
            comboQuestionLoading: false
        }
    },
    mounted() {
        this.loadTree()
        this.loadGlobalStats()
    },
    computed: {
        // 组合题型下钻：按年级聚合（去重年级、累加 comboCount）
        gradeGroups() {
            const map = new Map()
            ;(this.comboTreeList || []).forEach(v => {
                if (!map.has(v.gradeId)) {
                    map.set(v.gradeId, { gradeId: v.gradeId, gradeName: v.gradeName, comboCount: 0 })
                }
                map.get(v.gradeId).comboCount += (v.comboCount || 0)
            })
            return Array.from(map.values())
        },
        // 组合题型下钻：当前选中年级下的分册列表
        volumeGroups() {
            if (this.comboDrillGradeId == null) return []
            return (this.comboTreeList || []).filter(v => v.gradeId === this.comboDrillGradeId)
        },
        // 第二块：单单词题型补齐任务卡片（来自后端 perWordTypeDetail，按题型拆分）
        perWordTypeCards() {
            const detail = this.globalStats.perWordTypeDetail || []
            return detail.map(d => ({
                type: d.type,
                current: d.current || 0,
                need: d.need || 0,
                targetStock: d.targetStock || 0,
                pending: d.pending || 0,
                rate: d.rate != null ? d.rate : 0
            }))
        },
        // 第三块：组合题型补齐任务卡片（来自后端 comboTypeDetail，按题型拆分；含 current/need/targetStock/pending/rate）
        comboTypeCards() {
            const detail = this.globalStats.comboTypeDetail || []
            return detail.map(d => ({
                type: d.type,
                current: d.current || 0,
                need: d.need || 0,
                targetStock: d.targetStock || 0,
                pending: d.pending || 0,
                rate: d.rate != null ? d.rate : 0
            }))
        },
        // 第二块附加：T5 短文填空补齐卡片（来自后端 passageFillTypeDetail，与单单词题型口径不同，独立成组）
        passageFillTypeCards() {
            const detail = this.globalStats.passageFillTypeDetail || []
            return detail.map(d => ({
                type: d.type,
                current: d.current || 0,
                need: d.need || 0,
                targetStock: d.targetStock || 0,
                pending: d.pending || 0,
                rate: d.rate != null ? d.rate : 0
            }))
        }
    },
    methods: {
        // ===== 全局进度 =====
        async loadGlobalStats() {
            this.globalStatsLoading = true
            try {
                const res = await reqStatsGlobal()
                const data = res?.data || res || {}
                this.globalStats = {
                    dedupTotal: data.dedupTotal || 0,
                    dedupCompleted: data.dedupCompleted || 0,
                    pendingDedup: data.pendingDedup || 0,
                    dedupRate: data.dedupRate != null ? data.dedupRate : 0,
                    diagTotal: data.diagTotal || 0,
                    diagCompleted: data.diagCompleted || 0,
                    pendingDiagnose: data.pendingDiagnose || 0,
                    diagRate: data.diagRate != null ? data.diagRate : 0,
                    perWordTotal: data.perWordTotal || 0,
                    perWordCompleted: data.perWordCompleted || 0,
                    perWordCompletionRate: data.perWordCompletionRate != null ? data.perWordCompletionRate : 0,
                    // T3 单单词补齐任务量
                    enabledWordCount: data.enabledWordCount || 0,
                    perWordTypeCount: data.perWordTypeCount || 0,
                    perWordTargetStock: data.perWordTargetStock || 0,
                    perWordTaskTarget: data.perWordTaskTarget || 0,
                    perWordTaskCurrent: data.perWordTaskCurrent || 0,
                    perWordTaskRate: data.perWordTaskRate != null ? data.perWordTaskRate : 0,
                    perWordTypeDetail: data.perWordTypeDetail || [],
                    // T3 组合题型补齐任务量
                    comboTotal: data.comboTotal || 0,
                    comboTargetStock: data.comboTargetStock || 0,
                    comboTypeCount: data.comboTypeCount || 0,
                    comboTaskTarget: data.comboTaskTarget || 0,
                    comboTaskCurrent: data.comboTaskCurrent || 0,
                    comboTaskRate: data.comboTaskRate != null ? data.comboTaskRate : 0,
                    comboTypeDetail: data.comboTypeDetail || [],
                    // T5 短文填空补齐统计（独立）
                    passageFillTotal: data.passageFillTotal || 0,
                    passageFillTargetStock: data.passageFillTargetStock || 0,
                    passageFillTypeCount: data.passageFillTypeCount || 0,
                    passageFillTaskTarget: data.passageFillTaskTarget || 0,
                    passageFillTaskCurrent: data.passageFillTaskCurrent || 0,
                    passageFillTaskRate: data.passageFillTaskRate != null ? data.passageFillTaskRate : 0,
                    passageFillTypeDetail: data.passageFillTypeDetail || []
                }
            } catch (e) {
                console.error('加载全局清洗进度失败', e)
            } finally {
                this.globalStatsLoading = false
            }
        },

        // ===== 树 =====
        async loadTree() {
            this.treeLoading = true
            try {
                const res = await reqResourceTree()
                this.treeData = res?.data || res || []
                // 默认展开所有年级节点，分册直接可见，点单元名称即可加载单词
                const expanded = this.treeData.map(g => g.nodeId)
                const firstGrade = this.treeData[0]
                if (firstGrade && firstGrade.children && firstGrade.children.length) {
                    expanded.push(firstGrade.children[0].nodeId)
                }
                this.defaultExpandedKeys = expanded
                // 默认选中第一个年级下的第一个单元，并加载其单词列表
                this.$nextTick(() => {
                    const firstGrade = this.treeData[0]
                    if (firstGrade && firstGrade.children && firstGrade.children.length) {
                        const firstVolume = firstGrade.children[0]
                        if (firstVolume.children && firstVolume.children.length) {
                            const firstUnit = firstVolume.children[0]
                            if (this.$refs.treeRef) {
                                this.$refs.treeRef.setCurrentKey(firstUnit.nodeId)
                            }
                            this.onNodeClick(firstUnit)
                        }
                    }
                })
            } catch (e) {
                console.error('加载资源树失败', e)
            } finally {
                this.treeLoading = false
            }
        },

        async onClearCache() {
            try {
                await reqClearTreeCache()
                this.$message.success('缓存已清除，重新加载结构')
                await this.loadTree()
            } catch (e) {
                this.$message.error('清除缓存失败')
            }
        },

        onTreeSearch() {
            const kw = (this.treeKeyword || '').trim()
            if (!kw) {
                this.searchActive = false
                this.searchList = []
                return
            }
            this.searchActive = true
            this.searchLoading = true
            reqSearchWordByName(kw).then(res => {
                this.searchList = res?.data || res || []
            }).catch(() => {
                this.searchList = []
            }).finally(() => {
                this.searchLoading = false
            })
        },

        onPickSearchWord(item) {
            // 定位：展开树并高亮对应的单元，同时打开该单词详情
            this.searchActive = false
            this.treeKeyword = ''
            this.activeUnitId = item.unitId
            this.activeGradeId = item.gradeId
            this.activeVolume = item.volume
            this.currentWord = null
            this.defaultExpandedKeys = ['G-' + item.gradeId, 'GV-' + item.gradeId + '-' + item.volume]
            this.$nextTick(() => {
                const node = this.$refs.treeRef && this.$refs.treeRef.getNode('U-' + item.unitId)
                if (node) this.$refs.treeRef.setCurrentKey('U-' + item.unitId)
            })
            this.loadWordList()
            this.openDetail({ id: item.id, word: item.word, chineseMeaning: item.chineseMeaning, partOfSpeech: item.partOfSpeech, phoneticUk: item.phoneticUk || '', phoneticUs: item.phoneticUs || '' })
        },

        nodeIcon(data) {
            if (data.nodeType === 'grade') return 'el-icon-folder'
            if (data.nodeType === 'volume') return 'el-icon-folder-opened'
            return 'el-icon-document'
        },

        onNodeClick(data) {
            if (data.nodeType === 'unit') {
                this.activeUnitId = data.bizId
                this.activeGradeId = data.gradeId
                this.activeVolume = data.volume
                this.currentWord = null
                this.wordPage = 1
                this.wordKeyword = ''
                this.wordStatsMap = {}
                this.reviewStatsMap = {}
                this.unitFilterStatus = null
                this.loadUnitStats()
                this.loadWordList()
            }
            // grade / volume 节点仅展开折叠，不触发右侧
        },

        // 加载单元级清洗统计
        async loadUnitStats() {
            if (!this.activeGradeId || !this.activeVolume || !this.activeUnitId) return
            this.unitStatsLoading = true
            try {
                const res = await reqStatsReviewByUnit({
                    gradeId: this.activeGradeId,
                    volume: this.activeVolume,
                    unitId: this.activeUnitId
                })
                const data = res?.data || res || {}
                this.unitStats = {
                    total: data.total || 0,
                    unReviewed: data.unReviewed || 0,
                    aiReviewed: data.aiReviewed || 0,
                    teacherReviewed: data.teacherReviewed || 0,
                    pendingManualConfirm: data.pendingManualConfirm || 0,
                    duplicateDiscarded: data.duplicateDiscarded || 0
                }
            } catch (e) {
                console.error('加载单元统计失败', e)
            } finally {
                this.unitStatsLoading = false
            }
        },

        // 点击统计卡片，打开详情抽屉并筛选对应状态
        filterByStatus(statusKey) {
            if (!this.unitStats[statusKey]) return
            this.detailFilterStatus = statusKey
            this.openUnitReviewDrawer()
        },

        // ===== 单词列表 =====
        async loadWordList() {
            if (!this.activeUnitId) return
            this.wordLoading = true
            try {
                const res = await reqWordListByUnit(this.activeUnitId)
                let words = res?.data || res || []
                if (this.wordKeyword) {
                    const kw = this.wordKeyword.toLowerCase()
                    words = words.filter(w =>
                        w.word?.toLowerCase().includes(kw) ||
                        w.chineseMeaning?.includes(kw)
                    )
                }
                this.wordTotal = words.length
                const start = (this.wordPage - 1) * this.wordPageSize
                this.wordList = words.slice(start, start + this.wordPageSize)
                await this.loadWordStatsBatch(this.wordList)
                await this.loadReviewStatsBatch(this.wordList)
            } catch (e) {
                console.error('加载单词列表失败', e)
            } finally {
                this.wordLoading = false
            }
        },

        async loadWordStatsBatch(words) {
            // 过滤已缓存的单词，只查未缓存的
            const uncached = words.filter(w => !this.wordStatsMap[w.id])
            if (!uncached.length) return
            try {
                const ids = uncached.map(w => w.id).join(',')
                const res = await reqStatsByWordIdsBatch(ids)
                const data = res?.data || res || []
                // 按 wordId 分组：{ wordId: { questionType: cnt, ... } }
                const groupByWord = {}
                data.forEach(item => {
                    const wid = item.wordId
                    if (!groupByWord[wid]) groupByWord[wid] = {}
                    groupByWord[wid][item.questionType] = item.cnt
                })
                uncached.forEach(w => {
                    this.$set(this.wordStatsMap, w.id, groupByWord[w.id] || {})
                })
            } catch (e) {
                console.error('批量加载题型统计失败', e)
            }
        },

        // 批量加载单词级清洗状态（方案B：一次请求多个单词）
        async loadReviewStatsBatch(words) {
            if (!words || words.length === 0) return
            const ids = words.map(w => w.id)
            try {
                const res = await reqStatsReviewByWords(ids)
                const data = res?.data || res || []
                data.forEach(item => {
                    const map = {
                        total: item.total || 0,
                        unReviewed: item.unReviewed || 0,
                        aiReviewed: item.aiReviewed || 0,
                        teacherReviewed: item.teacherReviewed || 0,
                        pendingManualConfirm: item.pendingManualConfirm || 0,
                        duplicateDiscarded: item.duplicateDiscarded || 0
                    }
                    this.$set(this.reviewStatsMap, item.wordId, map)
                })
            } catch (e) {
                console.error('批量加载清洗状态失败', e)
            }
        },

        onWordSearch() {
            this.wordPage = 1
            this.loadWordList()
        },

        onWordPageChange(page) {
            this.wordPage = page
            this.loadWordList()
        },

        // ===== 详情 =====
        async openDetail(word) {
            this.currentWord = word
            this.detailDrawerVisible = true
            this.detailActiveType = null
            this.detailPage = 1
            this.includeDiscarded = false
            await this.loadDetailStats()
            await this.loadDetailList()
        },

        onWordRowClick(row) {
            this.openDetail(row)
        },

        backToList() {
            this.detailDrawerVisible = false
            this.currentWord = null
            this.detailList = []
            this.detailTypeTabs = []
        },

        async loadDetailStats() {
            if (!this.currentWord) return
            try {
                const res = await reqStatsByWord(this.currentWord.id)
                const data = res?.data || res || []
                this.detailTypeTabs = [
                    { type: null, label: '全部', count: data.reduce((s, i) => s + (i.cnt || 0), 0), duplicateCount: 0 }
                ]
                data.forEach(item => {
                    const info = QUESTION_TYPE_MAP[item.questionType] || { label: '题型' + item.questionType }
                    this.detailTypeTabs.push({
                        type: item.questionType,
                        label: info.label,
                        count: item.cnt || 0,
                        duplicateCount: 0
                    })
                })
            } catch (e) {
                console.error('加载统计失败', e)
            }
        },

        async loadDetailList() {
            if (!this.currentWord) return
            this.detailLoading = true
            try {
                const params = {
                    wordId: this.currentWord.id,
                    pageNum: this.detailPage,
                    pageSize: this.detailPageSize
                }
                if (this.detailActiveType) {
                    params.questionType = this.detailActiveType
                }
                // 单元级统计卡片跳转时，按状态筛选
                if (this.detailFilterStatus === 'unReviewed') params.reviewStatus = 0
                else if (this.detailFilterStatus === 'aiReviewed') params.reviewStatus = 1
                else if (this.detailFilterStatus === 'teacherReviewed') params.reviewStatus = 2
                else if (this.detailFilterStatus === 'pendingManualConfirm') params.dedupStatus = 2
                else if (this.detailFilterStatus === 'duplicateDiscarded') params.discardedOnly = true
                // 包含已废弃：默认 false（仅启用题），开启后后端不过滤 status=1
                if (this.includeDiscarded) params.includeDiscarded = true
                const res = await reqPageByWord(params)
                const page = res?.data || res || {}
                const records = page.records || []
                // 诊断总览：基于后端 review_status / duplicate_of / similarity 统计
                const sum = { unReviewed: 0, aiReviewed: 0, teacherReviewed: 0, duplicate: 0, pendingManualConfirm: 0 }
                records.forEach(r => {
                    if (r.reviewStatus === 2) sum.teacherReviewed++
                    else if (r.reviewStatus === 1) sum.aiReviewed++
                    else sum.unReviewed++
                    // 待人工确认：dedup_status=2 且未废弃
                    if (r.dedupStatus === 2 && r.status != 2) {
                        sum.pendingManualConfirm++
                        r.isPendingManual = true
                    }
                    if (r.duplicateOf) sum.duplicate++
                    // 疑似：相似度处于人工确认区间(0.7~0.9)且非精确重复
                    r.isDuplicate = !r.duplicateOf && r.similarity != null && r.similarity >= 0.7 && r.similarity < 0.9
                })
                this.reviewSummary = sum
                this.detailList = records
                this.detailTotal = page.total || 0
            } catch (e) {
                console.error('加载详情失败', e)
            } finally {
                this.detailLoading = false
            }
        },

        switchDetailType(type) {
            this.detailActiveType = type
            // 切换题型时清除从单元统计卡片带入的状态筛选，避免两者叠加
            this.detailFilterStatus = null
            this.detailPage = 1
            this.loadDetailList()
        },

        // 切换"包含已废弃"开关时重新加载详情列表
        onToggleIncludeDiscarded() {
            this.detailPage = 1
            this.loadDetailList()
        },

        onDetailPageChange(page) {
            this.detailPage = page
            this.loadDetailList()
        },

        // ===== AI 修订对比 =====

        // 字符级 diff：返回 HTML，把不同部分用红色下划线标出
        renderDiff(oldText, newText, side) {
            const oldStr = String(oldText || '')
            const newStr = String(newText || '')
            if (!oldStr && !newStr) return '-'
            if (oldStr === newStr) return this.escapeHtml(oldStr) || '（无变化）'

            const ops = this.computeDiffOps(oldStr, newStr)
            let html = ''
            ops.forEach(op => {
                const text = this.escapeHtml(op.text)
                if (op.type === 'equal') {
                    html += text
                } else if (side === 'old' && op.type === 'delete') {
                    html += `<span class="diff-del">${text}</span>`
                } else if (side === 'new' && op.type === 'insert') {
                    html += `<span class="diff-add">${text}</span>`
                }
                // 另一侧的差异字符不显示（用公共子序列自然跳过）
            })
            return html || '（无变化）'
        },

        // 简单 LCS 字符级 diff：返回 [{type:'equal'|'insert'|'delete', text}]
        computeDiffOps(a, b) {
            const n = a.length
            const m = b.length
            // 常见短文本直接判断
            if (n === 0) return [{ type: 'insert', text: b }]
            if (m === 0) return [{ type: 'delete', text: a }]

            // 使用滚动数组优化空间
            const dp = [new Array(m + 1).fill(0), new Array(m + 1).fill(0)]
            const maxLen = Math.max(n, m)
            // 太长时退化到整段标记，避免 O(n*m) 卡顿
            if (maxLen > 800) {
                return [{ type: 'delete', text: a }, { type: 'insert', text: b }]
            }

            let prev = 0
            let curr = 1
            for (let i = 1; i <= n; i++) {
                for (let j = 1; j <= m; j++) {
                    if (a[i - 1] === b[j - 1]) {
                        dp[curr][j] = dp[prev][j - 1] + 1
                    } else {
                        dp[curr][j] = Math.max(dp[prev][j], dp[curr][j - 1])
                    }
                }
                prev ^= 1
                curr ^= 1
            }

            // 回溯还原操作
            const ops = []
            let i = n
            let j = m
            while (i > 0 || j > 0) {
                if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
                    ops.push({ type: 'equal', text: a[i - 1] })
                    i--
                    j--
                } else if (j > 0 && (i === 0 || dp[prev][j - 1] >= dp[prev][j])) {
                    ops.push({ type: 'insert', text: b[j - 1] })
                    j--
                } else {
                    ops.push({ type: 'delete', text: a[i - 1] })
                    i--
                }
            }
            ops.reverse()

            // 合并连续同类型操作
            const merged = []
            ops.forEach(op => {
                const last = merged[merged.length - 1]
                if (last && last.type === op.type) {
                    last.text += op.text
                } else {
                    merged.push({ type: op.type, text: op.text })
                }
            })
            return merged
        },

        escapeHtml(str) {
            return str.replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;')
        },

        async openAiCompare(row) {
            try {
                const res = await reqQuestionDetail(row.id)
                this.aiCompareRow = res?.data || res || row
            } catch (e) {
                this.aiCompareRow = row
            }
            this.aiCompareVisible = true
        },

        // ===== 老师诊断确认 / 撤销 =====
        async onTeacherConfirm(row) {
            const isConfirm = row.reviewStatus !== 2
            const tip = isConfirm
                ? '确认将该题标记为教师二次诊断通过？'
                : '确定撤销二次诊断吗？撤销后将退回 AI 已诊断状态。'
            try {
                await this.$confirm(tip, isConfirm ? '确认诊断' : '撤销诊断', {
                    confirmButtonText: isConfirm ? '确认诊断' : '撤销',
                    cancelButtonText: '取消',
                    type: isConfirm ? 'primary' : 'warning'
                })
            } catch (e) {
                return
            }
            try {
                if (isConfirm) {
                    await reqConfirmTeacherReview(row.id)
                    this.$message.success('已标记为教师二次诊断通过，将优先用于备课与组卷')
                } else {
                    await reqRevokeTeacherReview(row.id)
                    this.$message.success('已撤销二次诊断，退回 AI 已诊断状态')
                }
                await this.loadDetailList()
            } catch (e) {
                this.$message.error('操作失败')
            }
        },

        // ===== 手动 AI 重新诊断 =====
        async onReDiagnose(row) {
            this.detailLoading = true
            try {
                await reqReDiagnose(row.id)
                this.$message.success('AI 重新诊断完成')
                await this.loadDetailList()
                await this.loadDetailStats()
            } catch (e) {
                this.$message.error('诊断失败')
            } finally {
                this.detailLoading = false
            }
        },

        // ===== 重复确认处理 =====
        async onResolveDuplicate(row) {
            try {
                await this.$confirm('确认将该重复题废弃，还是恢复启用？', '重复处理', {
                    distinguishCancelAndClose: true,
                    confirmButtonText: '废弃',
                    cancelButtonText: '恢复启用',
                    type: 'warning'
                }).then(() => reqResolveDuplicate(row.id, 'discard'))
                  .catch(action => {
                      if (action === 'cancel') return reqResolveDuplicate(row.id, 'restore')
                      return Promise.reject('close')
                  })
                this.$message.success('处理成功')
                await this.loadDetailList()
                await this.loadDetailStats()
            } catch (e) {
                if (e !== 'close') this.$message.error('处理失败')
            }
        },

        // ===== 待人工确认题：直接确认废弃 =====
        async onConfirmPending(row) {
            try {
                await this.$confirm('确认将该"待人工确认"题标记为废弃？', '确认废弃', {
                    confirmButtonText: '确认废弃',
                    cancelButtonText: '取消',
                    type: 'warning'
                })
            } catch (e) {
                return
            }
            try {
                await reqResolveDuplicate(row.id, 'discard')
                this.$message.success('已标记废弃')
                await this.loadDetailList()
                await this.loadDetailStats()
                // 同步刷新单元、单词级和全局统计
                await this.loadUnitStats()
                this.loadGlobalStats()
                if (this.currentWord) {
                    const res = await reqStatsReviewByWords([this.currentWord.id])
                    const data = res?.data || res || []
                    if (data.length) {
                        const item = data[0]
                        this.$set(this.reviewStatsMap, item.wordId, {
                            total: item.total || 0,
                            unReviewed: item.unReviewed || 0,
                            aiReviewed: item.aiReviewed || 0,
                            teacherReviewed: item.teacherReviewed || 0,
                            pendingManualConfirm: item.pendingManualConfirm || 0,
                            duplicateDiscarded: item.duplicateDiscarded || 0
                        })
                    }
                }
            } catch (e) {
                this.$message.error('操作失败')
            }
        },

        // 从单元统计卡片跳转，打开抽屉并展示该状态题目
        async openUnitReviewDrawer() {
            // 用第一个单词打开抽屉（展示全部），然后在抽屉内根据 detailFilterStatus 筛选
            if (this.wordList && this.wordList.length) {
                this.openDetail(this.wordList[0])
            }
        },

        async handleDeleteItem(row) {
            try {
                const res = await reqDeleteQuestion(row.id)
                if (res.code === 200 || res.code === 0) {
                    this.$message.success('删除成功')
                    await this.loadDetailStats()
                    await this.loadDetailList()
                    // 同步单元级统计卡片与全局统计，与废弃处理保持一致
                    await this.loadUnitStats()
                    this.loadGlobalStats()
                    if (this.currentWord) {
                        const cres = await reqStatsByWord(this.currentWord.id)
                        const data = cres?.data || cres || []
                        const map = {}
                        data.forEach(item => { map[item.questionType] = item.cnt })
                        this.$set(this.wordStatsMap, this.currentWord.id, map)
                        const rres = await reqStatsReviewByWords([this.currentWord.id])
                        const rdata = rres?.data || rres || []
                        if (rdata.length) {
                            const item = rdata[0]
                            this.$set(this.reviewStatsMap, item.wordId, {
                                total: item.total || 0,
                                unReviewed: item.unReviewed || 0,
                                aiReviewed: item.aiReviewed || 0,
                                teacherReviewed: item.teacherReviewed || 0,
                                pendingManualConfirm: item.pendingManualConfirm || 0,
                                duplicateDiscarded: item.duplicateDiscarded || 0
                            })
                        }
                    }
                } else {
                    this.$message.error(res?.msg || '删除失败')
                }
            } catch (e) {
                this.$message.error('删除失败')
            }
        },

        // ===== 题型标签 =====
        questionTypeLabel(type) {
            return (QUESTION_TYPE_MAP[type] || {}).label || ('题型' + type)
        },
        questionTypeShort(type) {
            return (QUESTION_TYPE_MAP[type] || {}).short || ('Q' + type)
        },

        // ===== 手动补齐生成 =====
        openGenerateDialog() {
            // 默认题型：好词好句(4)
            this.genForm = { questionType: 4, count: 10 }
            this.genDialogVisible = true
        },

        async submitGenerate() {
            if (!this.currentWord) {
                this.$message.warning('请先选择单词')
                return
            }
            if (!this.genForm.questionType) {
                this.$message.warning('请选择题型')
                return
            }
            this.genSubmitting = true
            try {
                const res = await reqGenerateForCombo(
                    [this.currentWord.id],
                    this.genForm.questionType,
                    this.genForm.count
                )
                const ok = res && (res.code === 200 || res.code === 0 || res.success)
                const data = res?.data || res?.result
                if (ok) {
                    // 后端异步生成：提交即返回"已提交"，题目在后台生成并入库，无需等待/轮询
                    this.$message.success('已提交生成，题目将在后台生成并进入诊断/去重流水线（稍后可手动刷新查看）')
                    this.genDialogVisible = false
                    // 注意：后台尚未生成完成，此处不自动刷新列表，避免刷到空数据
                } else {
                    this.$message.error((res && (res.msg || res.message)) || '提交失败')
                }
            } catch (e) {
                this.$message.error('提交失败：' + (e && e.message ? e.message : e))
            } finally {
                this.genSubmitting = false
            }
        },

        // ===== 单单词补齐失败跳过管理 =====
        openFailManage() {
            this.failPage = 1
            this.failManageVisible = true
            this.loadFailList()
        },

        async loadFailList() {
            this.failLoading = true
            try {
                const res = await reqGenerateFailPage({ pageNum: this.failPage, pageSize: this.failPageSize })
                const data = res?.data || res || {}
                const records = data.records || data.list || []
                this.failList = records
                this.failTotal = data.total || records.length
            } catch (e) {
                console.error('加载跳过记录失败', e)
                this.$message.error('加载跳过记录失败')
            } finally {
                this.failLoading = false
            }
        },

        onFailPageChange(page) {
            this.failPage = page
            this.loadFailList()
        },

        async onResetFail(row) {
            const key = row.wordId + '|' + row.questionType
            this.failResettingId = key
            try {
                const res = await reqGenerateFailReset(row.wordId, row.questionType)
                if (res && (res.code === 200 || res.code === 0 || res.success)) {
                    this.$message.success('已解除跳过，该(单词,题型)恢复参与补齐')
                    await this.loadFailList()
                    this.loadGlobalStats()
                } else {
                    this.$message.error((res && (res.msg || res.message)) || '解除失败')
                }
            } catch (e) {
                this.$message.error('解除失败')
            } finally {
                this.failResettingId = null
            }
        },

        // ===== 第四块组合题型下钻：题型 → 年级 → 分册 → 单词组 → 试题 =====
        openComboDrill(type) {
            this.comboDrillType = type
            this.comboDrillTitle = '题型下钻 · ' + this.questionTypeLabel(type)
            this.comboDrillVisible = true
            this.comboDrillGradeId = null
            this.comboDrillVolume = null
            this.comboKeyPage = 1
            this.comboDrillKey = null
            this.currentComboWords = ''
            this.comboQuestionList = []
            this.comboQuestionTotal = 0
            this.loadComboTree()
        },

        closeComboDrill() {
            this.comboDrillVisible = false
        },

        // 加载三级层级：年级 → 分册
        async loadComboTree() {
            if (!this.comboDrillType) return
            this.comboTreeLoading = true
            try {
                const res = await reqComboTree({ questionType: this.comboDrillType })
                const list = res?.data || res || []
                this.comboTreeList = list
                // 默认选中第一个年级+分册
                if (list && list.length) {
                    this.comboDrillGradeId = list[0].gradeId
                    this.comboDrillVolume = list[0].volume
                    this.loadComboKeys()
                } else {
                    this.comboKeyList = []
                    this.comboKeyTotal = 0
                }
            } catch (e) {
                console.error('加载年级分册层级失败', e)
                this.$message.error('加载层级失败')
            } finally {
                this.comboTreeLoading = false
            }
        },

        // 选择某个年级+分册，刷新右侧单词组列表
        onPickGradeVolume(node) {
            this.comboDrillGradeId = node.gradeId
            this.comboDrillVolume = node.volume
            this.comboKeyPage = 1
            this.comboDrillKey = null
            this.currentComboWords = ''
            this.comboQuestionList = []
            this.comboQuestionTotal = 0
            this.loadComboKeys()
        },

        // 点击年级：聚合该年级下分册总数，默认选中第一个分册并刷新单词组
        onPickGrade(grade) {
            this.comboDrillGradeId = grade.gradeId
            const vols = this.comboTreeList.filter(v => v.gradeId === grade.gradeId)
            if (vols && vols.length) {
                this.comboDrillVolume = vols[0].volume
            }
            this.comboKeyPage = 1
            this.comboDrillKey = null
            this.currentComboWords = ''
            this.comboQuestionList = []
            this.comboQuestionTotal = 0
            this.loadComboKeys()
        },

        async loadComboKeys() {
            if (!this.comboDrillType) return
            this.comboKeyLoading = true
            try {
                const params = {
                    questionType: this.comboDrillType,
                    pageNum: this.comboKeyPage,
                    pageSize: this.comboKeyPageSize
                }
                if (this.comboDrillGradeId != null) params.gradeId = this.comboDrillGradeId
                if (this.comboDrillVolume != null && this.comboDrillVolume !== '') params.volume = this.comboDrillVolume
                const res = await reqComboKeys(params)
                const data = res?.data || res || {}
                const list = data.list || []
                this.comboKeyList = list
                this.comboKeyTotal = data.total || list.length
            } catch (e) {
                console.error('加载单词组列表失败', e)
                this.$message.error('加载单词组失败')
            } finally {
                this.comboKeyLoading = false
            }
        },

        onComboKeyPageChange(page) {
            this.comboKeyPage = page
            this.loadComboKeys()
        },

        // 选择某单词组：记录覆盖单词、加载右侧试题
        onPickComboKey(item) {
            this.comboDrillKey = item.comboKey
            this.currentComboWords = item.wordDetails || ''
            this.comboQuestionPage = 1
            this.loadComboQuestions()
        },

        async loadComboQuestions() {
            if (!this.comboDrillKey) return
            this.comboQuestionLoading = true
            try {
                const res = await reqPageByCombo({
                    comboKey: this.comboDrillKey,
                    questionType: this.comboDrillType,
                    pageNum: this.comboQuestionPage,
                    pageSize: this.comboQuestionPageSize
                })
                const page = res?.data || res || {}
                this.comboQuestionList = page.records || []
                this.comboQuestionTotal = page.total || 0
            } catch (e) {
                console.error('加载组合试题失败', e)
                this.$message.error('加载试题失败')
            } finally {
                this.comboQuestionLoading = false
            }
        },

        onComboQuestionPageChange(page) {
            this.comboQuestionPage = page
            this.loadComboQuestions()
        },

        // 把后端 "word|释义、word|释义" 串格式化为 "word(释义) / word(释义)"
        formatComboWords(wordDetails) {
            if (!wordDetails) return '-'
            return wordDetails.split('、').map(s => {
                const idx = s.indexOf('|')
                if (idx < 0) return s
                const w = s.slice(0, idx)
                const m = s.slice(idx + 1)
                return m ? `${w}(${m})` : w
            }).join(' / ')
        }
    }
}
</script>

<style lang="scss" scoped>
.knowledge-page {
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 15px;
    height: 100%;
    box-sizing: border-box;
    overflow: hidden;
}

// 顶部全局清洗进度大卡片
.global-stats-card {
    flex-shrink: 0;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.06);
    padding: 14px 18px;
    display: flex;
    align-items: center;
    gap: 24px;

    .gsc-title {
        font-size: 15px;
        font-weight: 700;
        color: #303133;
        white-space: nowrap;
        padding-right: 18px;
        border-right: 1px solid #EBEEF5;
    }
    .gsc-body {
        display: flex;
        flex: 1;
        gap: 14px;
        flex-wrap: wrap;
    }
    .gsc-item {
        flex: 1;
        min-width: 150px;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 8px 6px;
        border-radius: 8px;
        background: #F5F7FA;
        border: 1px solid #EBEEF5;
        cursor: default;

        .gsc-num {
            font-size: 22px;
            font-weight: 700;
            color: #303133;
            line-height: 1.2;
            b { font-size: 13px; font-weight: 400; color: #909399; margin-left: 2px; }
        }
        .gsc-label {
            font-size: 12px;
            color: #909399;
            margin-top: 2px;
        }
        .gsc-rate {
            font-size: 11px;
            color: #4F6CF7;
            margin-top: 2px;
        }
        .gsc-meta {
            font-size: 11px;
            color: #E6A23C;
            margin-top: 2px;
        }
    }
    .gsc-dedup { background: #FEF0F0; border-color: #FBC4C4; .gsc-num { color: #F56C6C; } }
    .gsc-diagnose { background: #FDF6EC; border-color: #FAE2C2; .gsc-num { color: #E6A23C; } }
    .gsc-perword { background: #F0F9EB; border-color: #C2E7B0; .gsc-num { color: #67C23A; } }
}

// 第二块 / 第三块：题型补齐任务量卡片（每个题型一个小卡片）
.gen-task-card {
    flex-shrink: 0;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.06);
    padding: 14px 18px;
    display: flex;
    align-items: center;
    gap: 24px;

    .gtc-title {
        font-size: 15px;
        font-weight: 700;
        color: #303133;
        white-space: nowrap;
        padding-right: 18px;
        border-right: 1px solid #EBEEF5;

        .gtc-sub {
            display: block;
            font-size: 12px;
            font-weight: 400;
            color: #909399;
            margin-top: 4px;
            white-space: normal;
        }
    }
    // 标题带副标题上下排列（第二/三/四块）：避免标题、按钮、副标题挤在一行
    .gtc-title-with-sub {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        gap: 6px;

        .gtc-title-main {
            display: flex;
            align-items: center;
            gap: 10px;
        }
        .gtc-sub {
            margin-top: 0;
        }
    }
    .gtc-body {
        display: flex;
        flex: 1;
        gap: 14px;
        flex-wrap: wrap;
    }
    .gtc-passagefill {
        background: #F5F0FF;
        border-color: #D6C8FA;
        .gtc-num { color: #8B5CF6; }
    }
    // 短文填空补齐（T5）整块卡片加紫色主题边框/背景，视觉上与组合题型卡片区分
    &.gtc-passagefill-card {
        background: linear-gradient(to right, #FAF8FF 0%, #FFFFFF 120px);
        border: 1px solid #E0D4FA;
        box-shadow: 0 2px 12px 0 rgba(139, 92, 246, 0.06);
    }
    .gtc-item {
        min-width: 150px;
        display: flex;
        flex-direction: column;
        padding: 8px 12px;
        border-radius: 8px;
        background: #F5F7FA;
        border: 1px solid #EBEEF5;
        cursor: default;

        .gtc-item-head {
            display: flex;
            align-items: baseline;
            gap: 8px;
        }
        .gtc-num {
            font-size: 20px;
            font-weight: 700;
            color: #303133;
            line-height: 1.2;
            b { font-size: 13px; font-weight: 400; color: #909399; margin-left: 2px; }
        }
        .gtc-label {
            font-size: 13px;
            color: #606266;
            font-weight: 600;
        }
        .gtc-row {
            font-size: 11px;
            color: #909399;
            margin-top: 3px;
        }
        .gtc-pending { color: #E6A23C; }
        .gtc-rate { color: #4F6CF7; font-weight: 600; }
        &.gtc-reserved {
            background: #FAFAFA;
            border-style: dashed;
            border-color: #DCdfE6;
            .gtc-num, .gtc-label { color: #C0C4CC; }
            .gtc-todo { color: #C0C4CC; font-style: italic; }
        }
        // 可点击下钻（第四块组合题型卡片）
        &.gtc-clickable {
            cursor: pointer;
            transition: all 0.2s;
            &:hover {
                border-color: #4F6CF7;
                box-shadow: 0 4px 12px rgba(79, 108, 247, 0.18);
                transform: translateY(-2px);
            }
            .gtc-drill-tip {
                margin-top: 4px;
                color: #4F6CF7;
                font-weight: 600;
            }
        }
    }
}

// 组合题型下钻弹窗（单弹窗三段布局：年级栏 | 分册+单词组 | 试题）
.combo-drill {
    display: flex;
    gap: 14px;
    min-height: 460px;
    max-height: 70vh;

    // 最左：年级栏
    .cd-grade {
        width: 120px;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        border-right: 1px solid #EBEEF5;
        padding-right: 12px;
        overflow: auto;
    }
    .cd-col-title {
        font-size: 13px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 10px;
        flex-shrink: 0;
    }
    .cd-grade-list {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .cd-grade-item {
        padding: 9px 10px;
        border-radius: 8px;
        background: #F5F7FA;
        border: 1px solid #EBEEF5;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        flex-direction: column;
        gap: 2px;
        &:hover { border-color: #C6D2FF; }
        &.active {
            background: #EEF2FF;
            border-color: #4F6CF7;
            .cd-grade-name { color: #4F6CF7; }
        }
        .cd-grade-name { font-size: 13px; color: #303133; }
        .cd-grade-count { font-size: 11px; color: #909399; }
    }

    // 中：分册 + 单词组列表
    .cd-left {
        width: 340px;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        border-right: 1px solid #EBEEF5;
        padding-right: 14px;
        overflow: hidden;

        .cd-volume-list {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 10px;
            flex-shrink: 0;
        }
        .cd-volume-item {
            padding: 5px 12px;
            border-radius: 14px;
            background: #F5F7FA;
            border: 1px solid #EBEEF5;
            font-size: 12px;
            color: #606266;
            cursor: pointer;
            transition: all 0.2s;
            &:hover { border-color: #C6D2FF; }
            &.active {
                background: #EEF2FF;
                border-color: #4F6CF7;
                color: #4F6CF7;
            }
        }
        .cd-key-list {
            flex: 1;
            overflow: auto;
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
        .cd-key-item {
            padding: 8px 10px;
            border-radius: 8px;
            background: #F5F7FA;
            border: 1px solid #EBEEF5;
            cursor: pointer;
            transition: all 0.2s;
            &:hover { border-color: #C6D2FF; }
            &.active {
                background: #EEF2FF;
                border-color: #4F6CF7;
                .cd-key-words { color: #4F6CF7; }
            }
            .cd-key-main {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
            }
            .cd-key-words {
                font-size: 12px;
                color: #303133;
                line-height: 1.4;
                word-break: break-word;
            }
            .cd-key-count {
                font-size: 11px;
                color: #909399;
                white-space: nowrap;
                flex-shrink: 0;
            }
        }
        .cd-key-empty {
            text-align: center;
            color: #C0C4CC;
            font-size: 13px;
            padding: 30px 0;
        }
    }

    .cd-right {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;

        .cd-right-title {
            font-size: 13px;
            font-weight: 600;
            color: #303133;
            margin-bottom: 10px;
            &.cd-right-empty {
                color: #C0C4CC;
                font-weight: 400;
                margin: auto;
            }
        }
        .panel-table {
            flex: 1;
            overflow: auto;
        }
    }
}

// 主内容区：左侧树 + 右侧内容（横向）
.kb-main {
    flex: 1;
    min-height: 0;
    display: flex;
    gap: 15px;
    overflow: hidden;
}

// 左侧树
.tree-panel {
    width: 300px;
    flex-shrink: 0;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .tree-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 15px;
        border-bottom: 1px solid #EBEEF5;

        .tree-title {
            font-size: 15px;
            font-weight: 600;
            color: #303133;
        }
    }

    .tree-search {
        padding: 10px 15px;
        border-bottom: 1px solid #EBEEF5;
    }

    .tree-body {
        flex: 1;
        overflow: auto;
        padding: 8px 4px;
    }

    .kb-tree {
        font-size: 14px;

        .tree-node {
            display: inline-flex;
            align-items: center;
            gap: 6px;

            i {
                color: #909399;
                font-size: 14px;
            }
            .tree-label {
                color: #303133;
            }
        }
    }

    .search-result {
        flex: 1;
        overflow: auto;
        padding: 6px 0;

        .search-empty {
            text-align: center;
            color: #C0C4CC;
            font-size: 13px;
            padding: 30px 0;
        }

        .search-item {
            padding: 8px 15px;
            cursor: pointer;
            border-bottom: 1px solid #F5F7FA;

            &:hover {
                background: #F5F7FA;
            }
            .si-word {
                font-weight: 600;
                color: #303133;
                font-size: 14px;
            }
            .si-meta {
                font-size: 12px;
                color: #909399;
                margin: 2px 0;
            }
            .si-meaning {
                font-size: 12px;
                color: #606266;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }
    }
}

// 右侧
.content-panel {
    flex: 1;
    min-width: 0;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 15px;

    .content-empty {
        margin: auto;
    }

    .panel-header {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 15px;
        font-weight: 600;
        margin-bottom: 12px;

        i {
            color: #4F6CF7;
            font-size: 18px;
        }
        .header-desc {
            font-weight: 400;
            font-size: 12px;
            color: #909399;
            margin-left: 6px;
        }
        .header-right {
            margin-left: auto;
        }
    }

    .panel-table {
        flex: 1;
        overflow: auto;
    }
}

.total-badge {
    font-size: 13px;
    color: #909399;
    margin-left: 10px;

    b {
        color: #4F6CF7;
    }
}

.word-text {
    font-weight: 600;
    color: #303133;
    font-size: 15px;
}

.phonetic {
    font-size: 12px;
    color: #909399;
    font-family: 'Times New Roman', serif;
}

.kb-stats-mini {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    justify-content: center;

    .kb-type-tag {
        font-size: 11px;
        padding: 2px 6px;
        border-radius: 8px;
        background: #F0F2F5;
        color: #606266;
        white-space: nowrap;

        b {
            margin-left: 2px;
            color: #4F6CF7;
        }
    }
    .kb-empty {
        font-size: 12px;
        color: #C0C4CC;
    }
}

.pagination-wrap {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
}

// 单元级统计卡片
.unit-stats-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 14px;

    .us-item {
        flex: 1;
        min-width: 90px;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 10px 4px;
        border-radius: 8px;
        background: #F5F7FA;
        border: 1px solid #EBEEF5;
        cursor: default;
        transition: all 0.2s;

        .us-num {
            font-size: 20px;
            font-weight: 700;
            color: #C0C4CC;
            line-height: 1.2;
        }
        .us-label {
            font-size: 12px;
            color: #909399;
            margin-top: 2px;
        }

        &.on {
            cursor: pointer;
            .us-num { color: #303133; }
            &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
        }
        &.us-total.on { background: #EEF2FF; border-color: #C6D2FF; .us-num { color: #4F6CF7; } }
        &.us-un.on { background: #F4F4F5; border-color: #DCDFE6; .us-num { color: #909399; } }
        &.us-ai.on { background: #FDF6EC; border-color: #FAE2C2; .us-num { color: #E6A23C; } }
        &.us-te.on { background: #F0F9EB; border-color: #C2E7B0; .us-num { color: #67C23A; } }
        &.us-pending.on { background: #FEF9E7; border-color: #F9E0A0; .us-num { color: #E6A23C; } }
        &.us-dup.on { background: #FEF0F0; border-color: #FBC4C4; .us-num { color: #F56C6C; } }
    }
}

// 单词行清洗状态标签
.word-review-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    justify-content: center;

    .wrs-tag {
        font-size: 11px;
        padding: 1px 5px;
        border-radius: 6px;
        font-weight: 600;
        white-space: nowrap;

        &.wrs-un { background: #F4F4F5; color: #909399; }
        &.wrs-ai { background: #FDF6EC; color: #E6A23C; }
        &.wrs-te { background: #F0F9EB; color: #67C23A; }
        &.wrs-pen { background: #FEF9E7; color: #E6A23C; }
        &.wrs-dup { background: #FEF0F0; color: #F56C6C; }

        &.zero {
            background: transparent;
            color: #C0C4CC;
            font-weight: 400;
            opacity: 0.5;
        }
    }
}

.detail-back {
    margin-bottom: 10px;
}

.drawer-body {
    padding: 15px;
    height: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .panel-table {
        flex: 1;
        overflow: auto;
    }
}

.word-info-bar {
    display: flex;
    align-items: center;
    gap: 4px;
    background: linear-gradient(135deg, #EEF2FF 0%, #F5F7FF 100%);
    border-radius: 8px;
    padding: 12px 18px;
    margin-bottom: 12px;
    flex-wrap: wrap;

    .wi-word {
        font-size: 20px;
        font-weight: 700;
        color: #4F6CF7;
    }
    .wi-chinese {
        font-size: 14px;
        color: #606266;
    }
    .wi-pos {
        font-size: 13px;
        color: #909399;
        font-style: italic;
    }
    .wi-phonetic {
        font-size: 12px;
        color: #909399;
        font-family: 'Times New Roman', serif;
    }
}

.review-summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
    font-size: 13px;
    color: #909399;

    .rs-item {
        padding: 4px 12px;
        border-radius: 14px;
        background: #F5F7FA;
        border: 1px solid #EBEEF5;

        b {
            margin-left: 2px;
            color: #303133;
        }
        &.on b {
            font-weight: 700;
        }
        &.rs-un.on { background: #F4F4F5; border-color: #DCDFE6; b { color: #909399; } }
        &.rs-ai.on { background: #FDF6EC; border-color: #FAE2C2; b { color: #E6A23C; } }
        &.rs-te.on { background: #F0F9EB; border-color: #C2E7B0; b { color: #67C23A; } }
        &.rs-dup.on { background: #FEF0F0; border-color: #FBC4C4; b { color: #F56C6C; } }
        &.rs-pending.on { background: #FEF9E7; border-color: #F9E0A0; b { color: #E6A23C; } }
    }
}

.gen-btn {
    margin-left: auto;
}

.include-discarded {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: 12px;
    font-size: 12px;
    color: #606266;
}

.include-discarded .id-label {
    white-space: nowrap;
}

.action-hint {
    margin: 10px 0;
    line-height: 1.6;
}

.action-hint .el-alert__title {
    font-size: 12px;
}

.skip-manage-btn {
    margin-left: 10px;
}

.fail-tip {
    margin-bottom: 12px;
}

.fail-tip .el-alert__title {
    font-size: 12px;
    line-height: 1.6;
}

.act-reset {
    color: #F56C6C;
}

.gen-tip {
    font-size: 12px;
    color: #909399;
    margin-left: 8px;
}

.type-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 15px;
}

.type-tab {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    font-size: 13px;
    transition: all 0.2s;

    &:hover {
        transform: translateY(-1px);
    }
    .tab-count {
        font-weight: 600;
    }
}

.type-tab-2 { border-left: 3px solid #4F6CF7; }
.type-tab-3 { border-left: 3px solid #4F6CF7; }
.type-tab-4 { border-left: 3px solid #10B981; }
.type-tab-5 { border-left: 3px solid #F59E0B; }
.type-tab-6 { border-left: 3px solid #EC4899; }
.type-tab-7 { border-left: 3px solid #8B5CF6; }
.type-tab-8 { border-left: 3px solid #EF4444; }
.type-tab-9 { border-left: 3px solid #06B6D4; }
.type-tab-10 { border-left: 3px solid #14B8A6; }

.cell-ai-score,
.cell-sim {
    font-size: 11px;
    color: #909399;
    margin-top: 2px;
}

// 操作栏：按钮一行展示，风格统一
.row-actions {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    gap: 4px;
    white-space: nowrap;

    .el-button--text {
        padding: 0 4px;
        margin: 0;
    }
    .act-confirm.is-revoke {
        color: #E6A23C;
    }
    .act-pending {
        color: #E6A23C;
        font-weight: 600;
    }
    .act-delete {
        color: #F56C6C;
    }
}

// AI 修订对比弹窗
.ai-compare {
    .ac-row {
        display: flex;
        gap: 12px;
        margin-bottom: 16px;
    }
    .ac-label {
        width: 72px;
        flex-shrink: 0;
        font-weight: 600;
        color: #303133;
        font-size: 14px;
        padding-top: 4px;
    }
    .ac-cols {
        flex: 1;
        display: flex;
        gap: 12px;
    }
    .ac-col {
        flex: 1;
        border: 1px solid #EBEEF5;
        border-radius: 6px;
        padding: 10px 12px;
        background: #FAFAFA;

        &.ac-new {
            border-color: #C6E2FF;
            background: #F0F7FF;
        }
        .ac-h {
            font-size: 12px;
            color: #909399;
            margin-bottom: 6px;
        }
        .ac-text {
            font-size: 13px;
            color: #303133;
            white-space: pre-wrap;
            word-break: break-word;
            line-height: 1.6;
        }
    }
    .ac-meta {
        display: flex;
        gap: 24px;
        font-size: 12px;
        color: #909399;
        border-top: 1px dashed #EBEEF5;
        padding-top: 10px;
    }
    .diff-del,
    .diff-add {
        color: #f56c6c;
        text-decoration: underline;
        text-decoration-color: #f56c6c;
    }
}

// 手机端：顶部卡片统计竖向堆叠、整页可滚动，方便查看进度
@media screen and (max-width: 768px) {
    .knowledge-page {
        height: auto;
        min-height: 100%;
        overflow: auto;
        padding: 10px;
        gap: 10px;
    }
    .global-stats-card,
    .gen-task-card {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
        padding: 12px;
    }
    .global-stats-card .gsc-title,
    .gen-task-card .gtc-title {
        border-right: none;
        padding-right: 0;
        padding-bottom: 8px;
        border-bottom: 1px solid #EBEEF5;
    }
    .global-stats-card .gsc-body,
    .gen-task-card .gtc-body {
        width: 100%;
    }
    .global-stats-card .gsc-item,
    .gen-task-card .gtc-item {
        min-width: 0;
        flex: 1 1 calc(50% - 7px);
    }
}
</style>
